import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { describeError, logger } from "utils/logger";
import { CAMERA_GRANTED_KEY } from "./CameraPermissionPage";
import { getClientHomePath } from "utils/client";

const ProtectedScanRoute = ({ children }) => {
    const location = useLocation();

    //first check localstorage has valid clientdetails then allow else navigate to login
    let clientDetails = localStorage.getItem("clientDetails");
    let userDetails = localStorage.getItem("userDetails");
    let parsedClient = clientDetails ? JSON.parse(clientDetails) : null;
    if (!clientDetails) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }
    if (parsedClient && parsedClient.id == 49 && !userDetails) {
        return <Navigate to={getClientHomePath(parsedClient)} state={{ from: location.pathname }} replace />;
    }
    // State to hold the permission status: 'checking', 'granted', or 'denied'
    const [permissionStatus, setPermissionStatus] = useState("checking");

    useEffect(() => {
        let isMounted = true;
        let status: PermissionStatus | undefined;

        // One-time grants leave the Permissions API at "prompt" (see CameraPermissionPage)
        const effectiveState = (state: string) =>
            state === "prompt" && sessionStorage.getItem(CAMERA_GRANTED_KEY) === "1"
                ? "granted"
                : state;

        // This function checks the actual, real-time permission status from the browser
        const checkCameraPermission = async () => {
            if (!navigator.permissions || !navigator.permissions.query) {
                logger.warn("camera_permission_api_unsupported");
                setPermissionStatus("denied");
                localStorage.setItem("cameraPermission", "denied");
                return;
            }

            try {
                status = await navigator.permissions.query({ name: "camera" } as any);
                logger.info("camera_permission", {
                    state: status.state,
                    effective: effectiveState(status.state),
                });
                if (!isMounted) return;
                setPermissionStatus(effectiveState(status.state));
                localStorage.setItem("cameraPermission", status.state);
                // Optional: Listen for changes in permission status while the user is on the page
                status.onchange = () => {
                    if (!isMounted) return;
                    setPermissionStatus(effectiveState(status.state));
                    localStorage.setItem("cameraPermission", status.state);
                };
            } catch (error) {
                logger.warn("camera_permission_query_failed", { error: describeError(error) });
                if (!isMounted) return;
                setPermissionStatus("denied");
                localStorage.setItem("cameraPermission", "denied");
            }
        };

        checkCameraPermission();

        return () => {
            isMounted = false;
            if (status) status.onchange = null;
        };
    }, []);

    if (permissionStatus === "checking") {
        return null;
    }

    if (permissionStatus !== "granted") {
        return (
            <Navigate
                to="/camera-permission"
                state={{ from: location.pathname }}
                replace
            />
        );
    }

    return children;
};

export default ProtectedScanRoute;